import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 16  -  Cherenkov Detectors
// ============================================

/** 16.1 – Cherenkov Radiation Theory */
export const CherenkovRadiationTheory = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 300;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Cherenkov Radiation  -  How It Works", W / 2, 24);

    // Medium rectangle
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath();
    ctx.roundRect(30, 50, 500, 160, 12);
    ctx.fill();
    ctx.strokeStyle = "#93C5FD";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(30, 50, 500, 160, 12);
    ctx.stroke();
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Dielectric medium (n > 1)", 40, 68);

    // Particle path
    const py = 130;
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(60, py);
    ctx.lineTo(400, py);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath();
    ctx.moveTo(400, py);
    ctx.lineTo(393, py - 5);
    ctx.lineTo(393, py + 5);
    ctx.closePath();
    ctx.fill();
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Charged particle", 405, py - 5);
    ctx.fillText("(v > c/n)", 405, py + 10);

    // Cherenkov cone (shock wave)
    const coneOrigin = 320;
    const coneAngle = Math.acos(1 / 1.5); // n=1.5
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    // Upper wavefront
    ctx.beginPath();
    ctx.moveTo(coneOrigin, py);
    ctx.lineTo(
      coneOrigin - 120 * Math.cos(0),
      py - 120 * Math.sin(coneAngle)
    );
    ctx.stroke();
    // Lower wavefront
    ctx.beginPath();
    ctx.moveTo(coneOrigin, py);
    ctx.lineTo(
      coneOrigin - 120 * Math.cos(0),
      py + 120 * Math.sin(coneAngle)
    );
    ctx.stroke();

    // Circular wavefronts
    ctx.strokeStyle = "#60A5FA";
    ctx.lineWidth = 1;
    [1, 2, 3, 4].forEach((i) => {
      const cx = coneOrigin - i * 30;
      const r = i * 30 * Math.tan(coneAngle);
      ctx.beginPath();
      ctx.arc(cx, py, Math.min(r, 80), 0, Math.PI * 2);
      ctx.stroke();
    });

    // Angle label
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(coneOrigin, py, 35, Math.PI, Math.PI + coneAngle);
    ctx.stroke();
    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("θ_c", coneOrigin - 45, py - 20);

    // Key formula
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 16px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("cos θ_c = 1/(nβ)     condition: β > 1/n", W / 2, 240);

    // Speed comparison
    ctx.fillStyle = "#475569";
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText(
      "Like a sonic boom  -  particle outruns light in the medium",
      W / 2,
      265
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "Emitted as blue/UV light; intensity ∝ sin²θ_c (Frank-Tamm formula)",
      W / 2,
      285
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={300}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.2 – Threshold Velocity and Beta */
export const CherenkovThreshold = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Cherenkov Threshold  -  When Light Is Emitted", W / 2, 24);

    // Key condition
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 16px Inter,sans-serif";
    ctx.fillText("β_threshold = 1/n     →     p_threshold = m/√(n²−1)", W / 2, 56);

    // Table of thresholds for different particles and materials
    const ty = 75;
    ctx.fillStyle = "#1E293B";
    ctx.beginPath();
    ctx.roundRect(30, ty, 480, 24, [8, 8, 0, 0]);
    ctx.fill();
    const headers = [
      "Medium",
      "n",
      "β_thr",
      "p_thr(π)",
      "p_thr(K)",
      "p_thr(p)",
    ];
    headers.forEach((h, i) => {
      const x = [80, 150, 210, 290, 370, 440][i];
      ctx.fillStyle = "white";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(h, x, ty + 16);
    });

    const materials = [
      {
        name: "Water",
        n: 1.33,
        pi: "0.16 GeV/c",
        K: "0.56 GeV/c",
        p: "1.06 GeV/c",
      },
      {
        name: "Glass",
        n: 1.5,
        pi: "0.13 GeV/c",
        K: "0.44 GeV/c",
        p: "0.84 GeV/c",
      },
      {
        name: "Aerogel",
        n: 1.05,
        pi: "0.44 GeV/c",
        K: "1.54 GeV/c",
        p: "2.93 GeV/c",
      },
      {
        name: "CO₂ gas",
        n: 1.00045,
        pi: "4.7 GeV/c",
        K: "16.4 GeV/c",
        p: "31.2 GeV/c",
      },
    ];

    materials.forEach((m, ri) => {
      const y = ty + 24 + ri * 26;
      ctx.fillStyle = ri % 2 === 0 ? "#F8FAFC" : "white";
      ctx.fillRect(30, y, 480, 26);
      const vals = [
        m.name,
        m.n.toFixed(m.n < 1.01 ? 5 : 2),
        (1 / m.n).toFixed(m.n < 1.01 ? 5 : 4),
        m.pi,
        m.K,
        m.p,
      ];
      vals.forEach((v, ci) => {
        ctx.fillStyle = ci === 0 ? "#3B82F6" : "#475569";
        ctx.font =
          ci === 0 ? "bold 11px Inter,sans-serif" : "11px Inter,sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(v, [80, 150, 210, 290, 370, 440][ci], y + 18);
      });
    });

    // Visual: β < 1/n vs β > 1/n
    const vy = 210;
    const cards = [
      {
        title: "β < 1/n",
        desc: "No Cherenkov light\nParticle is too slow",
        color: "#94A3B8",
        icon: "�-",
      },
      {
        title: "β = 1/n",
        desc: "Threshold  -  light at\nθ = 0° (forward)",
        color: "#F59E0B",
        icon: "→",
      },
      {
        title: "β → 1",
        desc: "Maximum angle\nθ_max = arccos(1/n)",
        color: "#22C55E",
        icon: "✓",
      },
    ];
    cards.forEach((cd, i) => {
      const x = 25 + i * 175,
        w = 158,
        h = 55;
      ctx.fillStyle = cd.color + "10";
      ctx.beginPath();
      ctx.roundRect(x, vy, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = cd.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, vy, w, h, 8);
      ctx.stroke();
      ctx.fillStyle = cd.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cd.icon + " " + cd.title, x + w / 2, vy + 18);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      cd.desc
        .split("\n")
        .forEach((line, li) => ctx.fillText(line, x + w / 2, vy + 34 + li * 12));
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.3 – Cherenkov Emission Angle */
export const CherenkovAngle = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Cherenkov Angle vs Particle Velocity", W / 2, 24);

    // Plot θ_c vs β for different n
    const m = { left: 70, right: 30, top: 50, bottom: 50 };
    const pw = W - m.left - m.right,
      ph = H - m.top - m.bottom;

    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + ph);
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.stroke();

    ctx.save();
    ctx.translate(20, m.top + ph / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("θ_c (degrees)", 0, 0);
    ctx.restore();
    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("β = v/c", m.left + pw / 2, H - 8);

    // Media
    const media = [
      { name: "Water (n=1.33)", n: 1.33, color: "#3B82F6" },
      { name: "Aerogel (n=1.05)", n: 1.05, color: "#22C55E" },
      { name: "Glass (n=1.5)", n: 1.5, color: "#F59E0B" },
    ];

    const maxAngle = 50; // degrees

    media.forEach((med) => {
      ctx.strokeStyle = med.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      let started = false;
      for (let px = 0; px <= pw; px++) {
        const beta = 0.5 + (px / pw) * 0.5; // β from 0.5 to 1.0
        if (beta < 1 / med.n) continue;
        const thetaRad = Math.acos(1 / (med.n * beta));
        const thetaDeg = (thetaRad * 180) / Math.PI;
        const x = m.left + px;
        const y = m.top + ph - (thetaDeg / maxAngle) * ph;
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();

      // Label at β=1
      const thetaMax = Math.acos(1 / med.n);
      const thetaMaxDeg = (thetaMax * 180) / Math.PI;
      const ly = m.top + ph - (thetaMaxDeg / maxAngle) * ph;
      ctx.fillStyle = med.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(med.name, m.left + pw - 5, ly - 5);
    });

    // Y-axis labels
    [0, 10, 20, 30, 40, 50].forEach((v) => {
      const y = m.top + ph - (v / maxAngle) * ph;
      ctx.fillStyle = "#94A3B8";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(v + "°", m.left - 6, y + 4);
      if (v > 0) {
        ctx.setLineDash([2, 3]);
        ctx.strokeStyle = "#E2E8F0";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(m.left, y);
        ctx.lineTo(m.left + pw, y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });

    // X-axis labels
    [0.5, 0.6, 0.7, 0.8, 0.9, 1.0].forEach((v) => {
      const x = m.left + ((v - 0.5) / 0.5) * pw;
      ctx.fillStyle = "#94A3B8";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(v.toFixed(1), x, m.top + ph + 16);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.4 – Threshold Cherenkov Counters */
export const ThresholdCherenkov = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Threshold Cherenkov Counter", W / 2, 24);

    // Detector diagram
    const dy = 110;
    // Gas vessel
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(60, dy - 40, 280, 80, 10);
    ctx.fill();
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(60, dy - 40, 280, 80, 10);
    ctx.stroke();
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Gas radiator (CO₂, N₂, ...)", 200, dy - 48);

    // Beam through
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(20, dy);
    ctx.lineTo(370, dy);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("beam", 22, dy - 10);

    // Mirror
    ctx.fillStyle = "#94A3B8";
    ctx.beginPath();
    ctx.arc(320, dy, 40, -0.4, 0.4);
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#64748B";
    ctx.stroke();
    ctx.fillStyle = "#64748B";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("mirror", 330, dy + 50);

    // Reflected light to PMT
    ctx.strokeStyle = "#60A5FA";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.moveTo(310, dy - 8);
    ctx.lineTo(410, dy - 60);
    ctx.stroke();
    ctx.setLineDash([]);

    // PMT
    ctx.fillStyle = "#475569";
    ctx.beginPath();
    ctx.roundRect(400, dy - 80, 60, 35, 6);
    ctx.fill();
    ctx.fillStyle = "white";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("PMT", 430, dy - 58);

    // Logic: yes/no
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Signal → above threshold", 385, dy + 10);
    ctx.fillStyle = "#EF4444";
    ctx.fillText("No signal → below threshold", 385, dy + 30);

    // PID logic
    const cards = [
      {
        title: "Veto mode",
        desc: "Remove lighter particles\nthat fire the counter",
        color: "#EF4444",
      },
      {
        title: "Tag mode",
        desc: "Select particles that\nfire the counter",
        color: "#22C55E",
      },
      {
        title: "Tunable n",
        desc: "Adjust gas pressure\nto change threshold",
        color: "#3B82F6",
      },
    ];
    cards.forEach((cd, i) => {
      const x = 20 + i * 180,
        y = 170,
        w = 165,
        h = 55;
      ctx.fillStyle = cd.color + "10";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = cd.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.stroke();
      ctx.fillStyle = cd.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(cd.title, x + w / 2, y + 16);
      ctx.fillStyle = "#475569";
      ctx.font = "9px Inter,sans-serif";
      cd.desc
        .split("\n")
        .forEach((line, li) =>
          ctx.fillText(line, x + w / 2, y + 32 + li * 12)
        );
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Binary PID: yes/no answer  -  simple but effective at fixed momentum",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.5 – RICH Detectors */
export const RICHDetector = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Ring-Imaging Cherenkov (RICH) Detector", W / 2, 24);

    // Radiator
    ctx.fillStyle = "#E0E7FF";
    ctx.fillRect(50, 60, 40, 140);
    ctx.strokeStyle = "#6366F1";
    ctx.lineWidth = 2;
    ctx.strokeRect(50, 60, 40, 140);
    ctx.fillStyle = "#6366F1";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Radiator", 70, 55);

    // Particle
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(20, 130);
    ctx.lineTo(100, 130);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("particle", 50, 215);

    // Cherenkov photons cone
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 1;
    for (let i = 0; i < 6; i++) {
      const angle = 0.4 + (i / 5) * 0.4;
      ctx.beginPath();
      ctx.moveTo(75, 130);
      ctx.lineTo(75 + 180, 130 - Math.tan(angle) * 180);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(75, 130);
      ctx.lineTo(75 + 180, 130 + Math.tan(angle) * 180);
      ctx.stroke();
    }

    // Spherical mirror
    ctx.strokeStyle = "#64748B";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(180, 130, 200, -0.35, 0.35);
    ctx.stroke();
    ctx.fillStyle = "#64748B";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Focusing mirror", 380, 75);

    // Photodetector plane
    ctx.fillStyle = "#F0FDF4";
    ctx.fillRect(140, 60, 20, 140);
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.strokeRect(140, 60, 20, 140);
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("Photo-", 150, 43);
    ctx.fillText("detector", 150, 55);

    // Ring pattern on detector
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(150, 130, 30, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(150, 130, 2, 0, Math.PI * 2);
    ctx.fillStyle = "#EF4444";
    ctx.fill();

    // Right side: ring radius concept
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(400, 50, 140, 170, 10);
    ctx.fill();
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(400, 50, 140, 170, 10);
    ctx.stroke();

    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Ring Image", 470, 70);

    // Draw ring
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(470, 135, 40, 0, Math.PI * 2);
    ctx.stroke();
    // Hit dots on ring
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(470 + Math.cos(a) * 40, 135 + Math.sin(a) * 40, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#22C55E";
      ctx.fill();
    }
    ctx.fillStyle = "#EF4444";
    ctx.beginPath();
    ctx.arc(470, 135, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("R ∝ θ_c", 470, 190);
    ctx.fillText("→ measures β", 470, 205);

    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Ring radius R = f · tan θ_c → precise β measurement → PID",
      W / 2,
      252
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "Used in LHCb, ALICE, HERMES  -  separates π/K/p over wide momentum range",
      W / 2,
      272
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-indigo-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.6 – Refractive Index Materials */
export const RefractiveIndexMaterials = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Radiator Materials  -  Refractive Index Spectrum", W / 2, 24);

    // Horizontal log-scale bar of materials
    const materials = [
      { name: "He gas", n: 1.000034, thetaMax: "0.5°", color: "#E2E8F0" },
      { name: "CO₂ gas", n: 1.00045, thetaMax: "1.7°", color: "#DBEAFE" },
      { name: "C₄F₁₀ gas", n: 1.00153, thetaMax: "3.2°", color: "#BAE6FD" },
      { name: "Aerogel", n: 1.05, thetaMax: "18°", color: "#A7F3D0" },
      { name: "Water", n: 1.33, thetaMax: "41°", color: "#93C5FD" },
      { name: "Glass", n: 1.5, thetaMax: "48°", color: "#818CF8" },
    ];

    const barX = 60,
      barW = 420;
    materials.forEach((m, i) => {
      const y = 50 + i * 32;
      const w = barW * (Math.log10((m.n - 1) * 1e6 + 1) / Math.log10(500001));

      ctx.fillStyle = m.color;
      ctx.beginPath();
      ctx.roundRect(barX, y, Math.max(w, 20), 24, 4);
      ctx.fill();
      ctx.strokeStyle = "#94A3B8";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.roundRect(barX, y, Math.max(w, 20), 24, 4);
      ctx.stroke();

      ctx.fillStyle = "#1E293B";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(m.name, barX - 5, y + 16);

      ctx.fillStyle = "#1E293B";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(
        "n=" + m.n.toFixed(m.n < 1.01 ? 5 : 2) + "  θ_max=" + m.thetaMax,
        barX + Math.max(w, 20) + 8,
        y + 16
      );
    });

    // Selection guide
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(30, H - 50, 480, 40, 8);
    ctx.fill();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Low n (gas) → high-momentum PID  |  High n (liquid/solid) → low-momentum PID",
      W / 2,
      H - 36
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText(
      "Choice of radiator depends on the momentum range you want to separate",
      W / 2,
      H - 18
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 16.7 – BL4S Cherenkov Detector (case study) */
export const BL4SCherenkov = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("BL4S Cherenkov Detector Setup", W / 2, 24);

    const dy = 100;
    // Beam pipe
    ctx.fillStyle = "#E2E8F0";
    ctx.fillRect(0, dy - 6, W, 12);
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, dy - 6);
    ctx.lineTo(W, dy - 6);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, dy + 6);
    ctx.lineTo(W, dy + 6);
    ctx.stroke();

    // Components
    const comps = [
      { x: 60, label: "S1\ntrigger", w: 12, h: 40, color: "#22C55E" },
      { x: 150, label: "Cherenkov\nradiator", w: 50, h: 50, color: "#3B82F6" },
      { x: 280, label: "S2\nTOF start", w: 12, h: 40, color: "#22C55E" },
      { x: 420, label: "S3\nTOF stop", w: 12, h: 40, color: "#EF4444" },
    ];

    comps.forEach((comp) => {
      ctx.fillStyle = comp.color + "30";
      ctx.fillRect(comp.x - comp.w / 2, dy - comp.h / 2, comp.w, comp.h);
      ctx.strokeStyle = comp.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(comp.x - comp.w / 2, dy - comp.h / 2, comp.w, comp.h);
      ctx.fillStyle = comp.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "center";
      comp.label
        .split("\n")
        .forEach((line, i) =>
          ctx.fillText(line, comp.x, dy + comp.h / 2 + 14 + i * 12)
        );
    });

    // PMT on Cherenkov
    ctx.fillStyle = "#475569";
    ctx.beginPath();
    ctx.roundRect(130, dy - 55, 40, 22, 4);
    ctx.fill();
    ctx.fillStyle = "white";
    ctx.font = "bold 8px Inter,sans-serif";
    ctx.fillText("SiPM", 150, dy - 40);
    ctx.strokeStyle = "#60A5FA";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(150, dy - 33);
    ctx.lineTo(150, dy - 25);
    ctx.stroke();
    ctx.setLineDash([]);

    // Logic
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath();
    ctx.roundRect(470, dy - 25, 70, 50, 8);
    ctx.fill();
    ctx.fillStyle = "white";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("Trigger", 505, dy - 5);
    ctx.fillText("Logic", 505, dy + 10);

    // Specs table
    const ty = 155;
    const specs = [
      { param: "Radiator", value: "CO₂ at atmospheric pressure (n ≈ 1.00045)", color: "#3B82F6" },
      { param: "PID function", value: "Veto pions (β > threshold) to select kaons/protons", color: "#22C55E" },
      { param: "Momentum range", value: "π threshold at ~4.7 GeV/c; K threshold at ~16 GeV/c", color: "#F59E0B" },
      { param: "Combined with", value: "TOF (S2–S3) for full π/K/p separation", color: "#8B5CF6" },
    ];

    specs.forEach((s, i) => {
      const y = ty + i * 26;
      ctx.fillStyle = i % 2 === 0 ? "#F8FAFC" : "white";
      ctx.fillRect(30, y, 500, 24);
      ctx.fillStyle = s.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(s.param, 140, y + 17);
      ctx.fillStyle = "#475569";
      ctx.font = "11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(s.value, 155, y + 17);
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Cherenkov + TOF provide complementary PID for the Z-SCANNER experiment",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

// ============================================
// Chapter 17  -  Beam Optics
// ============================================

/** 17.1 – Dipole Magnets */
export const DipoleBending = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Dipole Magnets  -  Bending the Beam", W / 2, 24);

    // Magnet structure
    ctx.fillStyle = "#EF4444" + "20";
    ctx.beginPath();
    ctx.roundRect(160, 50, 200, 30, 6);
    ctx.fill(); // North pole
    ctx.fillStyle = "#3B82F6" + "20";
    ctx.beginPath();
    ctx.roundRect(160, 180, 200, 30, 6);
    ctx.fill(); // South pole
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(160, 50, 200, 30, 6);
    ctx.stroke();
    ctx.strokeStyle = "#3B82F6";
    ctx.beginPath();
    ctx.roundRect(160, 180, 200, 30, 6);
    ctx.stroke();

    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("N", 260, 72);
    ctx.fillStyle = "#3B82F6";
    ctx.fillText("S", 260, 200);

    // B field arrows (downward)
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 5; i++) {
      const x = 185 + i * 35;
      ctx.beginPath();
      ctx.moveTo(x, 85);
      ctx.lineTo(x, 170);
      ctx.stroke();
      ctx.fillStyle = "#8B5CF6";
      ctx.beginPath();
      ctx.moveTo(x, 175);
      ctx.lineTo(x - 4, 168);
      ctx.lineTo(x + 4, 168);
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("B field ↓", 260, 142);

    // Curved beam path
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(100, 130);
    ctx.quadraticCurveTo(260, 90, 450, 130);
    ctx.stroke();

    // Arrow on beam
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath();
    ctx.moveTo(450, 130);
    ctx.lineTo(443, 126);
    ctx.lineTo(443, 134);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("beam", 455, 134);

    // Key formula
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 16px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Bρ = p/q     (magnetic rigidity)", W / 2, 232);

    ctx.fillStyle = "#475569";
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText(
      "Radius of curvature ρ = p/(qB)  -  higher momentum → larger radius",
      W / 2,
      255
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "Dipoles select momentum: different p particles follow different paths",
      W / 2,
      275
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.2 – Quadrupole Magnets */
export const QuadrupoleFocusing = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Quadrupole Magnets  -  Focusing the Beam", W / 2, 24);

    // Cross-section view
    const qcx = 140,
      qcy = 140,
      qr = 70;

    // Pole pieces
    const poles = [
      { angle: Math.PI / 4, label: "N", color: "#EF4444" },
      { angle: (3 * Math.PI) / 4, label: "S", color: "#3B82F6" },
      { angle: (5 * Math.PI) / 4, label: "N", color: "#EF4444" },
      { angle: (7 * Math.PI) / 4, label: "S", color: "#3B82F6" },
    ];
    poles.forEach((p) => {
      const px = qcx + Math.cos(p.angle) * qr;
      const py = qcy + Math.sin(p.angle) * qr;
      ctx.beginPath();
      ctx.arc(px, py, 20, 0, Math.PI * 2);
      ctx.fillStyle = p.color + "30";
      ctx.fill();
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = p.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(p.label, px, py + 5);
    });

    // Beam aperture
    ctx.beginPath();
    ctx.arc(qcx, qcy, 30, 0, Math.PI * 2);
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#94A3B8";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("beam", qcx, qcy + 4);

    // Focusing arrows
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    // Horizontal: defocusing
    ctx.beginPath();
    ctx.moveTo(qcx - 25, qcy);
    ctx.lineTo(qcx - 45, qcy);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(qcx + 25, qcy);
    ctx.lineTo(qcx + 45, qcy);
    ctx.stroke();
    // Vertical: focusing
    ctx.strokeStyle = "#EF4444";
    ctx.beginPath();
    ctx.moveTo(qcx, qcy - 25);
    ctx.lineTo(qcx, qcy - 10);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(qcx, qcy + 25);
    ctx.lineTo(qcx, qcy + 10);
    ctx.stroke();

    ctx.fillStyle = "#22C55E";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("defocus", qcx - 55, qcy - 10);
    ctx.fillStyle = "#EF4444";
    ctx.fillText("focus", qcx + 30, qcy - 20);

    ctx.fillStyle = "#475569";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Cross-section", qcx, qcy + qr + 30);

    // Right side: key points
    const rx = 280;
    const points = [
      {
        title: "Focus in one plane",
        desc: "A quadrupole focuses\nhorizontally but defocuses\nvertically (or vice versa)",
        color: "#3B82F6",
      },
      {
        title: "Alternating gradient",
        desc: "Pair F+D quadrupoles\nfor net focusing in\nboth planes (FODO)",
        color: "#22C55E",
      },
      {
        title: "Gradient field",
        desc: "B = 0 at center,\nincreases linearly\nwith distance (B ∝ x)",
        color: "#F59E0B",
      },
    ];
    points.forEach((p, i) => {
      const y = 50 + i * 72,
        w = 240,
        h = 62;
      ctx.fillStyle = p.color + "10";
      ctx.beginPath();
      ctx.roundRect(rx, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(rx, y, w, h, 8);
      ctx.stroke();
      ctx.fillStyle = p.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(p.title, rx + 10, y + 16);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      p.desc
        .split("\n")
        .forEach((line, li) => ctx.fillText(line, rx + 10, y + 32 + li * 12));
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.3 – Beam Envelope and Emittance */
export const BeamEmittance = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Beam Envelope and Emittance", W / 2, 24);

    // Phase space ellipse
    const ecx = 150,
      ecy = 140,
      ea = 80,
      eb = 40;
    ctx.fillStyle = "#3B82F620";
    ctx.beginPath();
    ctx.ellipse(ecx, ecy, ea, eb, -0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(ecx, ecy, ea, eb, -0.3, 0, Math.PI * 2);
    ctx.stroke();

    // Axes
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(ecx - 100, ecy);
    ctx.lineTo(ecx + 100, ecy);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ecx, ecy + 70);
    ctx.lineTo(ecx, ecy - 70);
    ctx.stroke();

    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("x", ecx + 102, ecy + 4);
    ctx.textAlign = "center";
    ctx.fillText("x'", ecx + 5, ecy - 72);

    // Emittance label
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("ε = π · a · b", ecx, ecy + 90);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Phase space area", ecx, ecy + 105);

    // Random dots inside ellipse
    ctx.fillStyle = "#3B82F680";
    for (let i = 0; i < 30; i++) {
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random());
      const dx = r * ea * 0.85 * Math.cos(angle);
      const dy = r * eb * 0.85 * Math.sin(angle);
      const px = ecx + dx * Math.cos(-0.3) - dy * Math.sin(-0.3);
      const pyy = ecy + dx * Math.sin(-0.3) + dy * Math.cos(-0.3);
      ctx.beginPath();
      ctx.arc(px, pyy, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Right: Beam envelope diagram
    const rx = 310;
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Beam Envelope Along Beamline", rx + 110, 55);

    // Envelope: widening and narrowing
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(rx, 130);
    ctx.bezierCurveTo(rx + 40, 90, rx + 80, 90, rx + 110, 130);
    ctx.bezierCurveTo(rx + 140, 160, rx + 180, 160, rx + 220, 130);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(rx, 150);
    ctx.bezierCurveTo(rx + 40, 190, rx + 80, 190, rx + 110, 150);
    ctx.bezierCurveTo(rx + 140, 120, rx + 180, 120, rx + 220, 150);
    ctx.stroke();

    // Q magnets
    ctx.fillStyle = "#3B82F6";
    ctx.fillRect(rx + 105, 125, 10, 30);
    ctx.fillRect(rx - 5, 125, 10, 30);
    ctx.fillRect(rx + 215, 125, 10, 30);
    ctx.font = "9px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Q", rx, 170);
    ctx.fillText("Q", rx + 110, 170);
    ctx.fillText("Q", rx + 220, 170);

    // Beam axis
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(rx, 140);
    ctx.lineTo(rx + 220, 140);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Emittance is conserved (Liouville's theorem)  -  beam quality is preserved",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.4 – FODO Cell */
export const FODOCell = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 240;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("The FODO Cell  -  Basic Accelerator Lattice", W / 2, 24);

    const cy = 110;
    // Drift spaces and magnets
    const elements = [
      { x: 40, w: 50, label: "½F", color: "#22C55E", type: "quad" },
      { x: 90, w: 80, label: "Drift (O)", color: "#E2E8F0", type: "drift" },
      { x: 170, w: 50, label: "D", color: "#EF4444", type: "quad" },
      { x: 220, w: 80, label: "Drift (O)", color: "#E2E8F0", type: "drift" },
      { x: 300, w: 50, label: "½F", color: "#22C55E", type: "quad" },
      { x: 370, w: 10, label: "", color: "transparent", type: "sep" },
      { x: 380, w: 50, label: "½F", color: "#22C55E", type: "quad" },
      { x: 430, w: 80, label: "Drift (O)", color: "#E2E8F0", type: "drift" },
      { x: 510, w: 50, label: "D", color: "#EF4444", type: "quad" },
    ];

    elements.forEach((el) => {
      if (el.type === "sep") return;
      const h = el.type === "quad" ? 50 : 30;
      ctx.fillStyle = el.type === "drift" ? el.color : el.color + "30";
      ctx.beginPath();
      ctx.roundRect(el.x, cy - h / 2, el.w, h, 4);
      ctx.fill();
      if (el.type === "quad") {
        ctx.strokeStyle = el.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(el.x, cy - h / 2, el.w, h, 4);
        ctx.stroke();
      }
      ctx.fillStyle =
        el.type === "quad" ? el.color : "#94A3B8";
      ctx.font =
        el.type === "quad"
          ? "bold 12px Inter,sans-serif"
          : "10px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(el.label, el.x + el.w / 2, cy + 5);
    });

    // Beam axis
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, cy);
    ctx.lineTo(W - 10, cy);
    ctx.stroke();
    ctx.setLineDash([]);

    // FODO cell bracket
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(40, cy + 40);
    ctx.lineTo(40, cy + 50);
    ctx.lineTo(350, cy + 50);
    ctx.lineTo(350, cy + 40);
    ctx.stroke();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("One FODO cell", 195, cy + 66);

    // Legend
    const ly = 185;
    const legend = [
      { label: "F = focusing quad", color: "#22C55E" },
      { label: "D = defocusing quad", color: "#EF4444" },
      { label: "O = drift space", color: "#94A3B8" },
    ];
    legend.forEach((l, i) => {
      const x = 50 + i * 175;
      ctx.fillStyle = l.color;
      ctx.fillRect(x, ly, 12, 12);
      ctx.fillStyle = "#475569";
      ctx.font = "11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(l.label, x + 18, ly + 11);
    });

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Repeated FODO cells create strong focusing  -  the basis of all circular accelerators",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-red-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={240}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.5 – Momentum Dispersion */
export const MomentumDispersion = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Momentum Dispersion and Chromaticity", W / 2, 24);

    // Dipole bending different momenta
    const dy = 120;

    // Dipole magnet
    ctx.fillStyle = "#8B5CF6" + "15";
    ctx.beginPath();
    ctx.roundRect(160, dy - 50, 120, 100, 10);
    ctx.fill();
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(160, dy - 50, 120, 100, 10);
    ctx.stroke();
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Dipole", 220, dy + 60);

    // Three beam paths
    const paths = [
      { label: "p₀+Δp (high)", curve: -35, color: "#EF4444", end: 162 },
      { label: "p₀ (nominal)", curve: 0, color: "#22C55E", end: 130 },
      { label: "p₀-Δp (low)", curve: 35, color: "#3B82F6", end: 98 },
    ];

    paths.forEach((p) => {
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(60, dy);
      ctx.quadraticCurveTo(220, dy + p.curve * 0.5, 400, p.end);
      ctx.stroke();

      ctx.fillStyle = p.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(p.label, 405, p.end + 4);
    });

    // Beam arrival
    ctx.fillStyle = "#1E293B";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("beam in", 40, dy - 10);

    // Key concepts
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("D(s) = Δx / (Δp/p₀)     (dispersion function)", W / 2, 200);

    ctx.fillStyle = "#475569";
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText(
      "Higher-momentum particles bend less → shifted orbit → dispersed beam",
      W / 2,
      225
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "Chromaticity ξ = ΔQ/(Δp/p₀)  -  tune shift due to momentum spread",
      W / 2,
      248
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.6 – Secondary Beam Production */
export const SecondaryBeamProduction = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Secondary Beam Production", W / 2, 24);

    const dy = 100;

    // Primary beam
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(20, dy);
    ctx.lineTo(120, dy);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Primary p (24 GeV/c)", 70, dy - 15);

    // Target
    ctx.fillStyle = "#F59E0B";
    ctx.fillRect(120, dy - 25, 20, 50);
    ctx.strokeStyle = "#D97706";
    ctx.lineWidth = 2;
    ctx.strokeRect(120, dy - 25, 20, 50);
    ctx.fillStyle = "#D97706";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("Target", 130, dy + 38);
    ctx.fillStyle = "#92400E";
    ctx.font = "9px Inter,sans-serif";
    ctx.fillText("(Be/Pb)", 130, dy + 50);

    // Secondary particles spraying
    const secondaries = [
      { name: "π⁺", angle: -0.3, color: "#22C55E", len: 160 },
      { name: "π⁻", angle: -0.15, color: "#22C55E", len: 170 },
      { name: "K⁺", angle: 0.0, color: "#F59E0B", len: 180 },
      { name: "K⁻", angle: 0.12, color: "#F59E0B", len: 165 },
      { name: "p", angle: 0.25, color: "#3B82F6", len: 155 },
      { name: "p̄", angle: 0.38, color: "#8B5CF6", len: 140 },
      { name: "e±", angle: -0.45, color: "#EC4899", len: 130 },
    ];

    secondaries.forEach((s) => {
      const endX = 140 + Math.cos(s.angle) * s.len;
      const endY = dy + Math.sin(s.angle) * s.len;
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(140, dy);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.fillStyle = s.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(s.name, endX + 5, endY + 4);
    });

    // Beamline selection elements
    ctx.fillStyle = "#8B5CF6" + "20";
    ctx.beginPath();
    ctx.roundRect(340, dy - 30, 40, 60, 6);
    ctx.fill();
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(340, dy - 30, 40, 60, 6);
    ctx.stroke();
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Dipole", 360, dy + 42);
    ctx.fillText("(p select)", 360, dy + 54);

    // Collimator
    ctx.fillStyle = "#475569";
    ctx.fillRect(420, dy - 35, 10, 20);
    ctx.fillRect(420, dy + 15, 10, 20);
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Collim.", 425, dy + 48);

    // Selected beam
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(430, dy);
    ctx.lineTo(530, dy);
    ctx.stroke();
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Selected beam", 480, dy - 12);

    // Composition pie-chart concept
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(30, 175, 500, 80, 10);
    ctx.fill();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("T9 Secondary Beam Composition (typical at 2 GeV/c)", W / 2, 195);

    const comp = [
      { name: "π", pct: "~76%", color: "#22C55E" },
      { name: "e", pct: "~15%", color: "#EC4899" },
      { name: "K", pct: "~4%", color: "#F59E0B" },
      { name: "p", pct: "~4%", color: "#3B82F6" },
      { name: "μ", pct: "~1%", color: "#8B5CF6" },
    ];
    comp.forEach((c2, i) => {
      const x = 55 + i * 105;
      ctx.fillStyle = c2.color;
      ctx.beginPath();
      ctx.arc(x, 220, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(c2.name, x, 224);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(c2.pct, x, 240);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-purple-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 17.7 – CERN PS Lattice (case study) */
export const CERNPSLattice = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("The CERN Proton Synchrotron (PS) Lattice", W / 2, 24);

    // PS ring (simplified)
    const rcx = 170,
      rcy = 145,
      rr = 85;
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.arc(rcx, rcy, rr, 0, Math.PI * 2);
    ctx.stroke();

    // Magnet units around ring
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * Math.PI * 2;
      const mx = rcx + Math.cos(a) * rr;
      const my = rcy + Math.sin(a) * rr;
      ctx.beginPath();
      ctx.arc(mx, my, 5, 0, Math.PI * 2);
      ctx.fillStyle = i % 2 === 0 ? "#22C55E" : "#EF4444";
      ctx.fill();
    }

    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("PS Ring", rcx, rcy - 5);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("628 m circ.", rcx, rcy + 10);
    ctx.fillText("100 FODO cells", rcx, rcy + 24);

    // Extraction arrow
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(rcx + rr, rcy);
    ctx.lineTo(rcx + rr + 60, rcy);
    ctx.stroke();
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath();
    ctx.moveTo(rcx + rr + 60, rcy);
    ctx.lineTo(rcx + rr + 55, rcy - 4);
    ctx.lineTo(rcx + rr + 55, rcy + 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#F59E0B";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("→ T9", rcx + rr + 35, rcy - 10);

    // Right: specs
    const rx = 280;
    const specs = [
      { param: "Commissioned", value: "1959  -  CERN's first synchrotron" },
      { param: "Circumference", value: "628.3 m (100 identical cells)" },
      { param: "Maximum energy", value: "26 GeV (protons)" },
      { param: "Lattice type", value: "Combined-function FODO" },
      { param: "Dipoles", value: "100 combined-function magnets" },
      { param: "Extraction", value: "Slow extraction to East Area (T9)" },
      { param: "Still in use", value: "Yes  -  injector for LHC chain" },
    ];

    specs.forEach((s, i) => {
      const y = 50 + i * 28;
      ctx.fillStyle = "#3B82F6";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(s.param, rx + 80, y + 12);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(s.value, rx + 88, y + 12);
    });

    // Legend
    ctx.fillStyle = "#22C55E";
    ctx.beginPath();
    ctx.arc(320, 260, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("F quad", 330, 264);

    ctx.fillStyle = "#EF4444";
    ctx.beginPath();
    ctx.arc(400, 260, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillText("D quad", 410, 264);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};
